import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  ApiPaginationResponse,
  BankCardType,
  DeliveryPriceResponse,
  OrderData,
  OrderPackagesDataType,
} from "../../../interface/site.interface";
import { CourierServices } from "../../../services/courier.service";
import { BankCardsServices } from "../../../services/bankCards.service";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";

import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import styles from "../courier/Courier.module.scss";
import SelectComponent from "../../../components/shared/select/SelectComponent";
import PhoneInputComponent from "../../../components/shared/input-phone-component/PhoneInputComponent";
import InputComponent from "../../../components/shared/input/InputComponent";
import OrderRow from "../../../components/pages/courier/OrderRow";
import MainButton from "../../../components/shared/button/MainButton";
import ArrowImage from "../../../assets/images/pages/login/CaretLeft.svg";
import Spinner from "../../../components/shared/spinner/Spinner";
import Card from "../../../components/pages/card/Card";
import PackagesModal from "../../../components/pages/admin-panels/courier/PackagesModal";
import MapComponent from "../../../components/shared/MapCompanent/MapCompanent";
import AddressAutocomplete from "../../../components/shared/AddressAutocomplete/AddressAutocomplete";
import { MapPoint } from "../../../interface/map";
import { WoltService } from "../../../services/wolt.service";
import WoltOrderRow, {
  WoltOrderRowProps,
} from "../../../components/pages/wolt/WoltOrderRow";
import CustomInputIconComponent from "../../../components/shared/custom-input-icon-component/CustomInputIconComponent";
import TimeSlotPicker from "../../../components/shared/timeSlot/TimeSlotPicker";
import { BUSINESS_HOURS } from "../../../constants/business-hours";
import { getBusinessEndTime, roundToNextSlot } from "../../../utils/utils";
import CardSelectModal from "../../../components/shared/card-select-modal/CardSelectModal";
import PaymentStatusModal from "../../../components/shared/payment-status-modal/PaymentStatusModal";
import NoCardsModal from "../../../components/shared/no-cards-modal/NoCardsModal";
import PaymentMethodModal from "../../../components/shared/payment-method-modal/PaymentMethodModal";
import { useServiceDebt } from "../../../hooks/useServiceDebt";
import ServiceDebtOverlay from "../../../components/shared/service-debt-overlay/ServiceDebtOverlay";

const now = new Date();

export const Wolt = () => {
  const { serviceDebt, orderDebt } = useServiceDebt();
  const [formValues, setFormValues] = useState({
    phone: "",
    address: "",
    customer: "",
    branch_id: 1,
    phone_prefix: "51",
    description: "",
    dropoff_lat: 0,
    dropoff_lon: 0,
    scheduled_time: "",
    street: "",
    city: "",
    amount: 10.8,
    urgent_order: 0,
  });
  const { data } = useSelector((state: RootState) => state.translationData);
  const { userInfo } = useSelector((state: RootState) => state.userProfile);

  const [isAddressSelected, setIsAddressSelected] = useState<boolean>(false);
  const [isMarkerMoved, setIsMarkerMoved] = useState<boolean>(false);
  const [packages, setPackages] = useState<OrderPackagesDataType[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<number[]>([]);
  const [allOrders, setAllOrders] = useState<WoltOrderRowProps[]>([]);
  const [addressCoordinates, setAddressCoordinates] = useState<{
    lat: number;
    lon: number;
    city: string;
  } | null>(null);
  const [phoneCode, setPhoneCode] = useState("51");
  const dispatch = useDispatch<AppDispatch>();

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(true);
  const [showAllPackages, setShowAllPackages] = useState(false);
  const [responseCalculate, setResponseCalculate] =
    useState<DeliveryPriceResponse | null>(null);
  const [paginationData, setPaginationData] =
    useState<ApiPaginationResponse<OrderData> | null>(null);
  const [selectedPackageObjects, setSelectedPackageObjects] = useState<
    OrderPackagesDataType[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cards, setCards] = useState<BankCardType[]>([]);
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isPaymentStatusOpen, setIsPaymentStatusOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
  const [isNoCardsModalOpen, setIsNoCardsModalOpen] = useState(false);
  const language = useSelector((state: RootState) => state.language.language);
  const navigate = useNavigate();
  const [point, setPoint] = useState<MapPoint | null>(null);

  const selectedDate = formValues.scheduled_time
    ? new Date(formValues.scheduled_time)
    : now;

  const maxTime = useMemo(() => {
    const endHour = getBusinessEndTime(selectedDate);
    const mt = new Date();
    mt.setHours(endHour, 0, 0, 0);
    return mt;
  }, [selectedDate]);

  useEffect(() => {
    const fetchData = async () => {
      await CourierServices.getAllPackages().then((res) => {
        setPackages(res?.data.original);
      });
      await WoltService.getAllOrders().then((res) => {
        setAllOrders(res.data);
        setPaginationData(res?.data);
        setOrderHistoryLoading(false);
      });
    };

    fetchData();
  }, [language]);
  const initialRender = useRef(true);

  const minDate = useMemo(() => {
    const now = new Date();
    const endTime = getBusinessEndTime(now);
    if (now.getHours() >= endTime) {
      now.setDate(now.getDate() + 1);
      now.setHours(BUSINESS_HOURS.START_TIME, 0, 0, 0);
      return now;
    }
    now.setHours(now.getHours() + 2, 0, 0, 0);
    return now;
  }, []);

  const minTime = useMemo(() => {
    const { START_TIME } = BUSINESS_HOURS;

    const now = new Date();
    const selected = new Date(selectedDate);

    // Create start-of-day time
    const businessStart = new Date(selected);
    businessStart.setHours(START_TIME, 0, 0, 0);

    const businessEnd = new Date(selected);
    businessEnd.setHours(getBusinessEndTime(selected), 0, 0, 0);

    // CASE 1 — not today → earliest is start of business
    if (selected.toDateString() !== now.toDateString()) {
      return businessStart;
    }

    // CASE 2 — today but before open → open time
    if (now.getHours() < START_TIME) {
      return businessStart;
    }
    let slot = roundToNextSlot(now);
    slot = new Date(slot.getTime() + 60 * 60 * 1000);
    if (slot.getMinutes() !== 0) {
      slot.setHours(slot.getHours() + 1);
      slot.setMinutes(0, 0, 0);
    }
    if (slot > businessEnd) {
      const nextDay = new Date(selected);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(START_TIME, 0, 0, 0);
      return nextDay;
    }

    // Skip the disabled 10:00–11:00 slot
    if (slot.getHours() === 10) {
      slot.setHours(11, 0, 0, 0);
    }

    return slot;
  }, [selectedDate]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) {
        dispatch(fetchTranslation());
      }
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  const handlePostOnChange = async (
    lat?: number,
    lng?: number,
    city?: string,
    address?: string,
  ) => {
    const selectedCity = city ? city : (point?.city ?? "");

    if (!formValues?.branch_id || selectedPackages.length === 0) return;

    if (!selectedCity) {
      return;
    }

    const payload = {
      dropoff_lat: lat ? lat : (point?.lat ?? 0),
      dropoff_lon: lng ? lng : (point?.lng ?? 0),
      scheduled_time: normalizeDate(),
      street: address ? address : formValues.address,
      city: selectedCity,
      package_ids: selectedPackages,
      // urgert_order: formValues.urgent_order,
    };

    try {
      const response = await WoltService.calculatePrice(payload);
      setFormValues((prev) => ({
        ...prev,
        dropoff_lat: payload.dropoff_lat,
        dropoff_lon: payload.dropoff_lon,
        street: address ? address : formValues.address,
        amount: response.data.summary_price,
        city: city ? city : (addressCoordinates?.city ?? ""),
      }));
      setResponseCalculate(response.data);
    } catch (error) {}
  };

  const normalizeDate = () => {
    if (!formValues.scheduled_time) {
      return "";
    }
    const scheduledStr = formValues.scheduled_time;
    const date = new Date(scheduledStr);
    const plus30utc = new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes() + 30,
        date.getSeconds(),
      ),
    );
    return plus30utc.toISOString().replace(/\.\d{3}Z$/, "Z");
  };

  const formatTimeInterval = () => {
    if (!formValues.scheduled_time) {
      return "";
    }
    const startDate = new Date(formValues.scheduled_time);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 saat

    const pad = (n: number) => n.toString().padStart(2, "0");

    const day = pad(startDate.getDate());
    const month = pad(startDate.getMonth() + 1);
    const year = startDate.getFullYear();
    const startHours = pad(startDate.getHours());
    const startMinutes = pad(startDate.getMinutes());
    const endHours = pad(endDate.getHours());
    const endMinutes = pad(endDate.getMinutes());

    return `${day}.${month}.${year} ${startHours}:${startMinutes} - ${endHours}:${endMinutes}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors: { [key: string]: string } = {};

    if (!formValues.phone || formValues.phone.trim().length < 7) {
      validationErrors.phone = "Telefon nömrəsini daxil edin";
    }
    if (!formValues.customer || formValues.customer.trim() === "") {
      validationErrors.customer = "Ad soyadı daxil edin";
    }
    if (selectedPackages.length === 0) {
      validationErrors.package_ids = data?.select_package || "Bağlama seçin";
    }
    if (!formValues.scheduled_time) {
      validationErrors.scheduled_time = "Tarix və vaxt seçin";
    }
    if (!formValues.address || formValues.address.trim() === "") {
      validationErrors.street = "Ünvan boş ola bilməz";
    }
    if (!formValues.description || formValues.description.trim() === "") {
      validationErrors.description = "Ünvan təsvirini daxil edin";
    }
    if (!responseCalculate) {
      validationErrors.amount = "Qiymət hesablanmayıb (adres məlumatları düzgün daxil edilməyib)";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      ...formValues,
      phone_prefix: phoneCode,
      scheduled_time: normalizeDate(),
      time_interval: formatTimeInterval(),
      package_ids: selectedPackages,
    };

    setPendingPayload(payload);

    if (responseCalculate?.is_promotion === 1 && (responseCalculate?.summary_price ?? 0) === 0) {
      setIsSubmitting(true);
      try {
        const original = (await WoltService.order(payload)) as any;
        if (original?.case === "success") {
          setPaymentErrorMessage("");
          setPaymentSuccess(true);
          setIsPaymentStatusOpen(true);
          await refreshOrders();
        } else {
          const rawErr = original?.original?.message || original?.message || original?.content;
          setPaymentErrorMessage(typeof rawErr === "string" ? rawErr : "");
          setPaymentSuccess(false);
          setIsPaymentStatusOpen(true);
        }
      } catch (error: any) {
        const backendError = error.original?.content || error?.content;
        setPaymentErrorMessage(typeof backendError === "string" ? backendError : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsPaymentMethodModalOpen(true);
    setIsSubmitting(false);
  };

  const handleSelectBalancePayment = async () => {
    if (!pendingPayload) return;
    setIsSubmitting(true);
    try {
      const original = (await WoltService.order({ ...pendingPayload, payment_provider: "balance" })) as any;
      setIsPaymentMethodModalOpen(false);
      if (original?.case === "success") {
        setPaymentErrorMessage("");
        setPaymentSuccess(true);
        setIsPaymentStatusOpen(true);
        await refreshOrders();
      } else {
        const rawErr = original?.original?.message || original?.message || original?.content;
        setPaymentErrorMessage(typeof rawErr === "string" ? rawErr : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      }
    } catch (error: any) {
      setIsPaymentMethodModalOpen(false);
      const backendError = error.original?.content || error?.content;
      setPaymentErrorMessage(typeof backendError === "string" ? backendError : "");
      setPaymentSuccess(false);
      setIsPaymentStatusOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectPashaPayment = async () => {
    setIsPaymentMethodModalOpen(false);
    if (!pendingPayload) return;
    setIsSubmitting(true);
    try {
      const original = (await WoltService.order({ ...pendingPayload, payment_provider: "pasha" })) as any;
      if (original?.case === "success" && original?.pay === true && original?.content) {
        window.location.href = original.content;
      } else if (original?.case === "success") {
        setPaymentErrorMessage("");
        setPaymentSuccess(true);
        setIsPaymentStatusOpen(true);
        await refreshOrders();
      } else {
        const rawErr = original?.original?.message || original?.message || original?.content;
        setPaymentErrorMessage(typeof rawErr === "string" ? rawErr : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      }
    } catch (error: any) {
      const backendError = error.original?.content || error?.content;
      setPaymentErrorMessage(typeof backendError === "string" ? backendError : "");
      setPaymentSuccess(false);
      setIsPaymentStatusOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectCardPayment = async () => {
    setIsPaymentMethodModalOpen(false);
    try {
      const response = await BankCardsServices.getBankCards();
      const cardsList = response.data?.data || [];
      if (cardsList.length === 0) { setIsNoCardsModalOpen(true); return; }
      setCards(cardsList);
      setIsCardModalOpen(true);
    } catch {
      setIsNoCardsModalOpen(true);
    }
  };

  const handleCardSelect = async (cardId: number) => {
    if (!pendingPayload) return;

    setIsCardPaymentLoading(true);
    try {
      const payloadWithCard = {
        ...pendingPayload,
        card_id: cardId,
        payment_provider: "card",
      };

      const original = (await WoltService.order(payloadWithCard)) as any;

      if (original?.case === "success") {
        setPaymentErrorMessage("");
        setPaymentSuccess(true);
        setIsPaymentStatusOpen(true);
        await refreshOrders();
      } else if (
        original?.original?.success === false ||
        original?.case === "warning" ||
        original?.case === "error" ||
        original?.status === "error"
      ) {
        let rawErr = original?.original?.message || original?.message || original?.content;
        if (typeof rawErr === "string") {
          try { const p = JSON.parse(rawErr); rawErr = p?.content || rawErr; } catch {}
        }
        setPaymentErrorMessage(typeof rawErr === "string" ? rawErr : "");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      } else {
        setPaymentErrorMessage("");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
      }
    } catch (error: any) {
      console.error("Kart odenisi xetasi:", error);

      let backendError = error.original?.content || error?.content;
      if (typeof backendError === "string") {
        try { const p = JSON.parse(backendError); backendError = p?.content || backendError; } catch {}
      }
      if (backendError && typeof backendError === "object") {
        setErrors(backendError);
        setIsCardModalOpen(false);
      } else if (typeof backendError === "string") {
        setPaymentErrorMessage(backendError);
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
        setIsCardModalOpen(false);
      } else {
        setPaymentErrorMessage("");
        setPaymentSuccess(false);
        setIsPaymentStatusOpen(true);
        setIsCardModalOpen(false);
      }
    } finally {
      setIsCardPaymentLoading(false);
    }
  };

  const handlePaymentRedirect = () => {
    setIsPaymentStatusOpen(false);
    if (paymentSuccess) {
      window.location.reload();
    }
  };

  const refreshOrders = async () => {
    setOrderHistoryLoading(true);
    try {
      const res = await WoltService.getAllOrders();
      setAllOrders(res.data);
      setPaginationData(res.data.original);
    } catch (error) {
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  useEffect(() => {
    if (!userInfo?.phone1_prefix) {
      return;
    }
    setFormValues((prev) => ({
      ...prev,
      phone: userInfo.phone1_number,
      customer: userInfo?.name + " " + userInfo?.surname,
    }));
    setPhoneCode(userInfo?.phone1_prefix);
  }, [userInfo?.phone1_prefix]);

  const handlePageChange = async (urlOrPage: string | number) => {
    setOrderHistoryLoading(true);
    try {
      let res;
      if (typeof urlOrPage === "string") {
        res = await WoltService.getAllOrders(urlOrPage);
      } else {
        const url = `/courier/order?page=${urlOrPage}`;
        res = await WoltService.getAllOrders(url);
      }
      setAllOrders(res.data);
      setPaginationData(res.data.original);
    } catch (error) {
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPackages.length > 0) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.packages_list;
        return newErrors;
      });
    }
  }, [selectedPackages]);

  const handleAddressChange = (newAddress: string) => {
    setFormValues((prev) => ({
      ...prev,
      address: newAddress,
    }));
  };

  const handleCoordinatesChange = (coords: {
    lat: number;
    lon: number;
    city: string;
  }) => {
    setAddressCoordinates(coords);
  };

  return (
    <>
      {(serviceDebt.hasDebt || orderDebt.hasDebt) && <ServiceDebtOverlay serviceDebt={serviceDebt} orderDebt={orderDebt} />}
      <TitleUpdater
        title={
          data?.colibri && data?.auth_left_sidebar_wolt
            ? `${data?.colibri} | ${data?.auth_left_sidebar_wolt}`
            : "Colibri"
        }
      />
      <form id={styles.purchase}>
        <div className={styles.row}>
          <SelectComponent
            label={data?.wolt_branch}
            name="area_id"
            disabled={true}
            options={[]}
            placeholder={data?.wolt_branch_name}
            onChange={(val: string | number) => {
              setFormValues((prev) => ({ ...prev, area_id: val.toString() }));
              setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors.area_id;
                return newErrors;
              });
            }}
            error={errors.area_id}
          />
          {/* <InputComponent
            label="Mobil nömrə"
            type="tel"
            name="phone"
            placeholder="telefon nömrəsi"
            value={formValues.phone}
            onChange={handleInputChange}
            error={errors.phone}
          /> */}
          <PhoneInputComponent
            label={data?.auth_courier_mobile_number}
            id="phone"
            name="phone"
            selectedCode={phoneCode}
            placeholder="0000000"
            value={userInfo?.phone1_number ?? ""}
            onChange={(e) =>
              setFormValues((prevValues) => ({
                ...prevValues,
                phone: e.target.value.replace(phoneCode, ""),
              }))
            }
            onCodeChange={(code) => setPhoneCode(code)}
            error={errors.phone}
            combinePrefixWithNumber={true}
          />
        </div>

        <div className={styles.row}>
          <div
            onClick={() => {
              if (formValues.branch_id) {
                handleOpenModal();
              }
            }}
            style={{ flex: 1 }}
          >
            <SelectComponent
              label={data?.auth_courier_package}
              name="purchase"
              options={[
                {
                  label:
                    selectedPackages.length > 0
                      ? `${selectedPackages.length} bağlama seçildi`
                      : `${data?.select_package}`,
                  value: "custom",
                },
              ]}
              value="custom"
              onChange={() => {
                setErrors((prevErrors) => {
                  const newErrors = { ...prevErrors };
                  delete newErrors.packages_list;
                  return newErrors;
                });
              }}
              placeholder={data?.auth_courier_select}
              error={errors.packages_list}
              disabled={!formValues.branch_id}
              selectedPackagesCount={selectedPackages.length}
            />
            <p className={styles.errorMsg}>{errors.package_ids}</p>
          </div>
          <div style={{ flex: 1 }}>
            <InputComponent
              label={data?.auth_referal_first_name}
              type="text"
              name="name"
              placeholder={data?.auth_referal_first_name}
              value={formValues.customer}
              onChange={(e) => {
                setFormValues((prev) => ({
                  ...prev,
                  customer: e.target.value,
                }));
                if (errors.customer) {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.customer;
                    return newErrors;
                  });
                }
              }}
              error={errors.customer}
            />
          </div>
        </div>

        {/*{!formValues.urgent_order && (*/}
        <div className={styles.row}>
          <div
            className={styles.dateinputWrapper}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {data?.auth_courier_date}
            <div className={styles.dateInputWrapper}>
              <TimeSlotPicker
                placeholder={data?.auth_courier_date}
                min={minDate.toISOString()}
                minTime={minTime}
                maxTime={maxTime}
                onChange={(date) => {
                  setFormValues((prev) => ({
                    ...prev,
                    scheduled_time: date.toString(),
                  }));
                }}
              />
            </div>
            <p className={styles.errorMsg}>{errors.scheduled_time}</p>
          </div>
        </div>
        {/*)}*/}
        {/*<div*/}
        {/*  style={{*/}
        {/*    marginTop: "20px",*/}
        {/*  }}*/}
        {/*>*/}
        {/*<label className={styles.selectBox}>*/}
        {/*  <CustomCheckbox*/}
        {/*    icon={checked}*/}
        {/*    text={data?.auth_courier_express_delivery}*/}
        {/*    color="green"*/}
        {/*    checked={formValues.urgent_order}*/}
        {/*    onChange={async (isChecked) => {*/}
        {/*      setFormValues((prev) => ({*/}
        {/*        ...prev,*/}
        {/*        scheduled_time: "",*/}
        {/*        urgent_order: isChecked,*/}
        {/*      }));*/}
        {/*      await handlePostOnChange();*/}
        {/*    }}*/}
        {/*    disabledColor="red" // disabled halda qırmızı rəng*/}
        {/*  />*/}
        {/*</label>*/}
        {/*</div>*/}
        {/*<div>*/}

        {/*</div>*/}

        <AddressAutocomplete
          value={formValues.address}
          isAddressSelected={isAddressSelected}
          isMarkerMoved={isMarkerMoved}
          onChange={(v) => {
            setFormValues((p) => ({ ...p, address: v }));
            setIsAddressSelected(false);
            if (errors.street || errors.city) {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.street;
                delete newErrors.city;
                return newErrors;
              });
            }
          }}
          onSelect={async ({ address, lat, lon, raw }) => {
            setFormValues((p) => ({ ...p, address, city: raw.address.city }));
            setIsAddressSelected(true);
            handleCoordinatesChange({ lat, lon, city: raw.address.city });
          }}
          viewbox={[49.7, 40.3, 50.1, 40.55]}
          lang="az"
          countryCodes="az"
          placeholder={data?.auth_courier_address}
        />
        <p className={styles.errorMsg}>{errors.street || errors.city}</p>
        <div id="map">
          <MapComponent
            setPoint={setPoint}
            point={point}
            isMarkerMoved={isMarkerMoved}
            setIsMarkerMoved={setIsMarkerMoved}
            handlePostOnChange={handlePostOnChange}
            isAddressSelected={isAddressSelected}
            setIsAddressSelected={setIsAddressSelected}
            address={formValues.address}
            setAddress={handleAddressChange}
            setCoordinates={handleCoordinatesChange}
            initialCoordinates={addressCoordinates}
          />
        </div>
        <div style={{ marginTop: "10px" }}>
          {/*<InputComponent*/}
          {/*  label={data?.wolt_description}*/}
          {/*  type="text"*/}
          {/*  name="description"*/}
          {/*  placeholder={data?.wolt_description}*/}
          {/*  value={formValues.description}*/}
          {/*  onChange={(e) => {*/}
          {/*    setFormValues((prev) => ({*/}
          {/*      ...prev,*/}
          {/*      description: e.target.value,*/}
          {/*    }));*/}
          {/*  }}*/}
          {/*/>*/}

          <CustomInputIconComponent
            name={"description"}
            placeholder={data?.wolt_description ?? ""}
            label={data?.wolt_address_description}
            type="textarea"
            value={formValues.description}
            onChange={(val) => {
              setFormValues((prev) => ({
                ...prev,
                description: val,
              }));
            }}
          />
          <p className={styles.errorMsg}>{errors.description}</p>
        </div>
        <div className={styles.SelectedAllPackages}>
          {orderHistoryLoading ? (
            <div className={styles.loadingWrapper}>
              {/* <Spinner size="large" /> */}
            </div>
          ) : selectedPackageObjects?.length === 0 ? (
            ""
          ) : (
            <table className={styles.order_history_list_select}>
              <thead>
                <tr>
                  <th>{data?.auth_common_tracking_code}</th>
                  <th>{data?.auth_amount}</th>
                  <th>{data?.auth_common_foreign_debt}</th>
                  <th>{data?.auth_common_local_debt}</th>
                </tr>
              </thead>
              <tbody>
                {selectedPackageObjects?.map((order, index) => (
                  <OrderRow key={index} {...order} SelectedPackages={true} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.form_bottom}>
          <div className={styles.purchase_details}>
            <p className={styles.purchase_delivery_cost}>
              {data?.auth_courier_foreign_delivery}
              <span>
                {responseCalculate?.delivery_price
                  ? responseCalculate.delivery_price.toFixed(2)
                  : "0.00"}{" "}
                AZN
              </span>
            </p>

            <p className={styles.purchase_courier_cost}>
              {data?.auth_courier_service}
              <span>
                {(responseCalculate?.courier_price ?? 0).toFixed(2)} AZN
              </span>
            </p>

            <p className={styles.purchase_total_cost}>
              {data?.auth_courier_total_amount}
              <span>
                {(responseCalculate?.summary_price ?? 0).toFixed(2)} AZN
              </span>
            </p>
            {errors.amount && (
              <p className={styles.errorMsg}>{errors.amount}</p>
            )}
          </div>
          <MainButton
            className={styles.form_submitBtn}
            text={data?.auth_promocode_confirm}
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
        </div>
        <div id={styles.order_list}>
          <div className={styles.pagination_part}>
            <p className={styles.pagination_title}>
              {data?.auth_courier_my_orders}
            </p>
            {(paginationData?.current_page === 1 &&
              paginationData?.next_page_url === null) ||
              (paginationData && (
                <div className={styles.pagination}>
                  <img
                    src={ArrowImage}
                    className={`${styles.pagination_arrow_left} ${
                      !paginationData.prev_page_url ? styles.disabled : ""
                    }`}
                    alt="Prev"
                    onClick={() => {
                      if (paginationData.prev_page_url) {
                        handlePageChange(paginationData.prev_page_url);
                      }
                    }}
                  />

                  {[...Array(paginationData.last_page)].map((_, index) => {
                    const page = index + 1;
                    return (
                      <p
                        key={page}
                        className={`${styles.pagination_count} ${
                          paginationData.current_page === page
                            ? styles.active
                            : ""
                        }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </p>
                    );
                  })}

                  <img
                    src={ArrowImage}
                    className={`${styles.pagination_arrow_right} ${
                      !paginationData.next_page_url ? styles.disabled : ""
                    }`}
                    alt="Next"
                    onClick={() => {
                      if (paginationData.next_page_url) {
                        handlePageChange(paginationData.next_page_url);
                      }
                    }}
                  />
                </div>
              ))}
          </div>

          {orderHistoryLoading ? (
            <div className={styles.loadingWrapper}>
              <Spinner size="large" />
            </div>
          ) : allOrders?.length === 0 ? (
            <div className={styles.empty}>{data?.auth_courier_no_orders}</div>
          ) : (
            <table className={styles.order_history_list}>
              <thead>
                <tr>
                  <th>{data?.auth_referal_full_name}</th>
                  <th>{data?.auth_courier_date}</th>
                  <th>{data?.phone}</th>
                  <th>{data?.status}</th>
                  <th>{data?.auth_courier_total_amount}</th>
                  <th>{data?.address}</th>
                  <th>{data?.wolt_track || "İzlə"}</th>
                </tr>
              </thead>
              <tbody>
                {allOrders?.map((order, index) => (
                  <WoltOrderRow key={index} {...order} data={data} />
                ))}
              </tbody>
            </table>
          )}

          <div className={styles.courier_card}>
            {allOrders?.map((order, index) => {
              const payload = {
                ...order,
                status: order.status_name,
                full_name: order.customer,
              };
              return <Card key={index} {...payload} isWolt={true} />;
            })}
          </div>
        </div>
      </form>
      {isSubmitting && (
        <div className={styles.loadingOverlay}>
          <Spinner size="large" />
        </div>
      )}

      {modalVisible && (
        <PackagesModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          packages={packages || []}
          selectedPackages={selectedPackages}
          setSelectedPackages={setSelectedPackages}
          handlePostOnChange={handlePostOnChange}
          setSelectedPackageObjects={setSelectedPackageObjects}
          showAllPackages={showAllPackages}
          setShowAllPackages={setShowAllPackages}
        />
      )}

      <PaymentMethodModal
        isOpen={isPaymentMethodModalOpen}
        onClose={() => setIsPaymentMethodModalOpen(false)}
        onSelectCard={handleSelectCardPayment}
        onSelectBalance={handleSelectBalancePayment}
        translationData={data}
        isLoading={isSubmitting}
      />

      <CardSelectModal
        isOpen={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
        onSelectCard={handleCardSelect}
        cards={cards}
        isLoading={isCardPaymentLoading}
        amount={`${(responseCalculate?.summary_price ?? 0).toFixed(2)} AZN`}
        translationData={data}
      />

      <PaymentStatusModal
        isOpen={isPaymentStatusOpen}
        isSuccess={paymentSuccess}
        onClose={() => setIsPaymentStatusOpen(false)}
        onRedirect={handlePaymentRedirect}
        errorMessage={paymentErrorMessage}
        translationData={data}
      />

      <NoCardsModal
        isOpen={isNoCardsModalOpen}
        onClose={() => setIsNoCardsModalOpen(false)}
        translationData={data}
      />
    </>
  );
};
